import * as React from 'react';
import Button from '@mui/material/Button';
import errorResponses from '../../../errorResponses';
import useDeleteExtraction from '../../hooks/useDeleteExtraction';
import { AppContext } from '../../../App';
import { useQueryClient } from '@tanstack/react-query';
import Delete from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface IProps {
  extractionId: string;
  extractionBatchLabel?: string;
  iconSize?: 'small' | 'regular';
}

const DeleteExtraction: React.FC<IProps> = ({
  extractionId,
  extractionBatchLabel,
  iconSize = 'regular',
}) => {
  const { setToast } = React.useContext(AppContext);
  const client = useQueryClient();
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const { mutate: deleteExtraction, isPending: isLoading } = useDeleteExtraction({
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['extractionBatch']
      });
      client.invalidateQueries({
        queryKey: ['allDocuments']
      });
      client.invalidateQueries({
        queryKey: ['allExtractions']
      });
      client.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Deleted',
      });
      handleCloseConfirmation();
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeleteExtraction,
      });
    },
    onMutate: () => {
      handleCloseConfirmation();
    },
  });

  const iconStyle = {
    color: theme.palette.custom.lightGreen,
    cursor: 'pointer',
    fontSize: iconSize === 'small' ? '21px' : undefined,
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={`Delete Extraction`}
        disableFocusListener
        disableTouchListener
      >
        <Delete
          data-cy={`btn-delete-extraction-${extractionBatchLabel}`}
          onClick={handleClickOpen}
          style={iconStyle}
        />
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this extraction?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => {
              deleteExtraction(extractionId);
            }}
            autoFocus
            data-cy="btn-extraction-batch-delete-modal-yes"
            disabled={isLoading}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteExtraction;
